import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { formatCurrency, formatPercentage } from '@/lib/utils';
import KpiPreferencesService from '@/services/KpiPreferencesService';
import type {
  AssetKpiData,
  AssetKpiPreference,
  CannedKpi,
  CreateKpiPreferenceRequest,
  PortfolioKpiData,
  UserKpiPreference,
} from '@/types/kpi';
import {
  BarChart3,
  Building,
  Calculator,
  DollarSign,
  Home,
  Minus,
  Plus,
  Target,
  Trash2,
  TrendingDown,
  TrendingUp,
  TrendingUpIcon,
  Users,
  Wallet,
} from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';

interface CustomizableKpiDashboardProps {
  scope: 'dashboard' | 'asset';
  assetId?: string;
  portfolioData?: PortfolioKpiData;
  assetData?: AssetKpiData;
  onRefresh?: () => void;
}

interface KpiCardProps {
  kpi: UserKpiPreference | AssetKpiPreference;
  value: number;
  unit?: string;
  trend?: {
    direction: 'up' | 'down' | 'stable';
    percentage: number;
  };
  onDelete?: () => void;
}

// Function to get icon for KPI type
const getKpiIcon = (kpiId: string) => {
  switch (kpiId) {
    case 'total_properties':
      return <Building className="h-4 w-4 text-muted-foreground" />;
    case 'total_portfolio_value':
      return <DollarSign className="h-4 w-4 text-muted-foreground" />;
    case 'portfolio_roi':
      return <TrendingUpIcon className="h-4 w-4 text-muted-foreground" />;
    case 'total_investment':
      return <Wallet className="h-4 w-4 text-muted-foreground" />;
    case 'portfolio_appreciation':
      return <BarChart3 className="h-4 w-4 text-muted-foreground" />;
    case 'average_cap_rate':
      return <Target className="h-4 w-4 text-muted-foreground" />;
    case 'total_monthly_cash_flow':
      return <DollarSign className="h-4 w-4 text-muted-foreground" />;
    case 'active_properties':
      return <Users className="h-4 w-4 text-muted-foreground" />;
    case 'average_property_value':
      return <Home className="h-4 w-4 text-muted-foreground" />;
    case 'total_annual_income':
      return <DollarSign className="h-4 w-4 text-muted-foreground" />;
    case 'total_annual_expenses':
      return <DollarSign className="h-4 w-4 text-muted-foreground" />;
    case 'net_operating_income':
      return <Calculator className="h-4 w-4 text-muted-foreground" />;
    default:
      return <BarChart3 className="h-4 w-4 text-muted-foreground" />;
  }
};

const KpiCard = ({ kpi, value, unit, trend, onDelete }: KpiCardProps) => {
  const getTrendIcon = () => {
    if (!trend) return null;
    switch (trend.direction) {
      case 'up':
        return <TrendingUp className="h-4 w-4 text-green-500" />;
      case 'down':
        return <TrendingDown className="h-4 w-4 text-red-500" />;
      default:
        return <Minus className="h-4 w-4 text-gray-500" />;
    }
  };

  const formatValue = (val: number, unitType?: string) => {
    if (unitType === '%') return formatPercentage(val);
    if (unitType === '$') return formatCurrency(val);
    return `${val.toLocaleString()}${unitType ? ` ${unitType}` : ''}`;
  };

  return (
    <Card className="relative group">
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <CardTitle className="text-sm font-medium flex items-center gap-2">
          {getKpiIcon(kpi.kpi_id)}
          {kpi.display_name}
        </CardTitle>
        <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
          <Button variant="ghost" size="sm" onClick={onDelete}>
            <Trash2 className="h-3 w-3" />
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-bold">{formatValue(value, unit)}</div>
        {trend && (
          <div className="flex items-center gap-1 mt-2 text-sm">
            {getTrendIcon()}
            <span
              className={
                trend.direction === 'up' ? 'text-green-500' : 'text-red-500'
              }
            >
              {trend.percentage.toFixed(1)}%
            </span>
            <span className="text-muted-foreground">vs last period</span>
          </div>
        )}
        {kpi.description && (
          <p className="text-xs text-muted-foreground mt-2">
            {kpi.description}
          </p>
        )}
      </CardContent>
    </Card>
  );
};

// Add KPI Card component
const AddKpiCard = ({
  onClick,
  disabled,
  className,
}: {
  onClick: () => void;
  disabled: boolean;
  className?: string;
}) => (
  <Card
    className={`border-dashed border-2 border-muted-foreground/25 hover:border-muted-foreground/50 transition-colors cursor-pointer ${className}`}
    onClick={disabled ? undefined : onClick}
  >
    <CardContent className="flex flex-col items-center justify-center p-6 text-center">
      <Plus className="h-8 w-8 text-muted-foreground mb-2" />
      {disabled ? (
        <>
          <h3 className="font-semibold text-muted-foreground">
            All KPIs Added
          </h3>
          <p className="text-sm text-muted-foreground">
            You've added all available KPIs
          </p>
        </>
      ) : (
        <>
          <h3 className="font-semibold">Add KPI</h3>
          <p className="text-sm text-muted-foreground">
            Add a new KPI to your dashboard
          </p>
        </>
      )}
    </CardContent>
  </Card>
);

export function CustomizableKpiDashboard({
  scope,
  assetId,
  portfolioData,
  assetData,
  onRefresh,
}: CustomizableKpiDashboardProps) {
  const [preferences, setPreferences] = useState<
    (UserKpiPreference | AssetKpiPreference)[]
  >([]);
  const [cannedKpis, setCannedKpis] = useState<CannedKpi[]>([]);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [selectedKpi, setSelectedKpi] = useState<CannedKpi | null>(null);
  const [customDisplayName, setCustomDisplayName] = useState('');
  const [loading, setLoading] = useState(true);
  const [settingUpDefaults, setSettingUpDefaults] = useState(false);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [prefs, kpis] = await Promise.all([
        scope === 'dashboard'
          ? KpiPreferencesService.getUserKpiPreferences('dashboard')
          : assetId
            ? KpiPreferencesService.getAssetKpiPreferences(assetId)
            : Promise.resolve([]),
        KpiPreferencesService.getCannedKpis(),
      ]);

      // If no preferences exist, set up defaults automatically
      if (prefs.length === 0) {
        setSettingUpDefaults(true);
        try {
          if (scope === 'dashboard') {
            await KpiPreferencesService.setupDefaultPreferences();
          } else if (assetId) {
            await KpiPreferencesService.setupDefaultAssetKpis(assetId);
          }

          // Reload preferences after setting up defaults
          const updatedPrefs =
            scope === 'dashboard'
              ? await KpiPreferencesService.getUserKpiPreferences('dashboard')
              : assetId
                ? await KpiPreferencesService.getAssetKpiPreferences(assetId)
                : [];

          setPreferences(updatedPrefs);
        } catch (error) {
          console.error('Error setting up default KPIs:', error);
          // Don't show error toast for default setup - it's expected for new users
        } finally {
          setSettingUpDefaults(false);
        }
      } else {
        setPreferences(prefs);
      }

      setCannedKpis(kpis);
    } catch (error) {
      console.error('Error loading KPI data:', error);
      toast.error('Failed to load KPI data');
    } finally {
      setLoading(false);
    }
  }, [scope, assetId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleAddKpi = async () => {
    if (!selectedKpi) return;

    try {
      const preference: CreateKpiPreferenceRequest = {
        scope,
        kpi_id: selectedKpi.name,
        kpi_type: 'canned',
        display_name: customDisplayName || selectedKpi.display_name,
        description: selectedKpi.description,
        configuration: {
          period: 'annual',
        },
      };

      if (scope === 'dashboard') {
        await KpiPreferencesService.createUserKpiPreference(preference);
      } else if (assetId) {
        await KpiPreferencesService.createAssetKpiPreference(assetId, {
          ...preference,
          asset_id: assetId,
        });
      }

      toast.success('KPI added successfully');
      setIsAddDialogOpen(false);
      setSelectedKpi(null);
      setCustomDisplayName('');
      loadData();
      onRefresh?.();
    } catch (error) {
      console.error('Error adding KPI:', error);
      toast.error('Failed to add KPI');
    }
  };

  const handleDeleteKpi = async (kpiId: number) => {
    try {
      if (scope === 'dashboard') {
        await KpiPreferencesService.deleteUserKpiPreference(kpiId);
      } else if (assetId) {
        await KpiPreferencesService.deleteAssetKpiPreference(assetId, kpiId);
      }

      toast.success('KPI removed successfully');
      loadData();
      onRefresh?.();
    } catch (error) {
      console.error('Error deleting KPI:', error);
      toast.error('Failed to remove KPI');
    }
  };

  const getKpiValue = (kpi: UserKpiPreference | AssetKpiPreference): number => {
    if (scope === 'dashboard' && portfolioData) {
      const key = kpi.kpi_id as keyof PortfolioKpiData;
      const value = portfolioData[key];
      return typeof value === 'number' ? value : 0;
    }
    if (scope === 'asset' && assetData) {
      const key = kpi.kpi_id as keyof AssetKpiData;
      const value = assetData[key];
      return typeof value === 'number' ? value : 0;
    }
    return 0;
  };

  const getKpiUnit = (kpi: UserKpiPreference | AssetKpiPreference) => {
    const cannedKpi = cannedKpis.find((ck) => ck.name === kpi.kpi_id);
    return cannedKpi?.unit;
  };

  // Get available KPIs (not already added)
  const availableKpis = cannedKpis.filter(
    (kpi) => !preferences.some((pref) => pref.kpi_id === kpi.name),
  );

  // Check if all KPIs are already added
  const allKpisAdded = availableKpis.length === 0;

  if (loading || settingUpDefaults) {
    return (
      <div className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <Card key={i} className="animate-pulse">
              <CardHeader className="pb-2">
                <div className="flex items-center gap-2">
                  <div className="h-4 w-4 bg-gray-200 rounded" />
                  <div className="h-4 bg-gray-200 rounded w-3/4" />
                </div>
              </CardHeader>
              <CardContent>
                <div className="h-8 bg-gray-200 rounded w-1/2" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Add KPI</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {allKpisAdded ? (
              <div className="text-center py-4">
                <Calculator className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-semibold mb-2">All KPIs Added</h3>
                <p className="text-muted-foreground">
                  You have already added all available KPIs. You can manage your
                  existing KPIs or remove some to add different ones.
                </p>
              </div>
            ) : (
              <>
                <div>
                  <Label htmlFor="kpi-select">Select KPI</Label>
                  <Select
                    onValueChange={(value) => {
                      const kpi = cannedKpis.find((k) => k.name === value);
                      setSelectedKpi(kpi || null);
                      setCustomDisplayName(kpi?.display_name || '');
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Choose a KPI" />
                    </SelectTrigger>
                    <SelectContent>
                      {cannedKpis.map((kpi) => {
                        const isAlreadyAdded = preferences.some(
                          (pref) => pref.kpi_id === kpi.name,
                        );
                        return (
                          <SelectItem
                            key={kpi.name}
                            value={kpi.name}
                            disabled={isAlreadyAdded}
                            className={
                              isAlreadyAdded
                                ? 'opacity-50 cursor-not-allowed'
                                : ''
                            }
                          >
                            <div className="flex items-center gap-2">
                              <span>{kpi.display_name}</span>
                              <Badge variant="secondary" className="text-xs">
                                {kpi.category}
                              </Badge>
                              {isAlreadyAdded && (
                                <Badge variant="outline" className="text-xs">
                                  Added
                                </Badge>
                              )}
                            </div>
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </div>

                {selectedKpi && (
                  <div>
                    <Label htmlFor="display-name">Display Name</Label>
                    <Input
                      id="display-name"
                      value={customDisplayName}
                      onChange={(e) => setCustomDisplayName(e.target.value)}
                      placeholder={selectedKpi.display_name}
                    />
                    {selectedKpi.description && (
                      <p className="text-sm text-muted-foreground mt-1">
                        {selectedKpi.description}
                      </p>
                    )}
                  </div>
                )}
              </>
            )}

            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => setIsAddDialogOpen(false)}
              >
                {allKpisAdded ? 'Close' : 'Cancel'}
              </Button>
              {!allKpisAdded && (
                <Button onClick={handleAddKpi} disabled={!selectedKpi}>
                  Add KPI
                </Button>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {preferences.length === 0 ? (
        <Card className="text-center">
          <div className="flex flex-col items-center gap-4">
            <Calculator className="h-12 w-12 text-muted-foreground" />
            <div>
              <h3 className="text-lg font-semibold">Setting up your KPIs</h3>
              <p className="text-muted-foreground">
                {scope === 'dashboard'
                  ? 'Setting up default KPIs for your dashboard...'
                  : 'Setting up default KPIs for this asset...'}
              </p>
            </div>
          </div>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
          {preferences.map((kpi) => (
            <KpiCard
              key={kpi.id}
              kpi={kpi}
              value={getKpiValue(kpi)}
              unit={getKpiUnit(kpi)}
              onDelete={() => handleDeleteKpi(kpi.id)}
            />
          ))}
          {preferences.length < 4 && (
            <AddKpiCard
              onClick={() => setIsAddDialogOpen(true)}
              disabled={allKpisAdded}
              className={allKpisAdded ? 'opacity-50 cursor-not-allowed' : ''}
            />
          )}
        </div>
      )}
    </div>
  );
}
